import { createSlice, PayloadAction, ThunkAction } from '@reduxjs/toolkit'
import { AuthState, MikaUser, MfaChallengeState } from '../../../shared/types'
import { Dispatch } from 'redux'
import { datadogRum } from '@datadog/browser-rum'
import { RootState } from '../../../store/store'

import { setUserList } from '../../User/ducks/userSlice'
import {
  setOrganizationList,
  setSelectedOrganization,
} from '../../Organization/ducks/organizationSlice'

import {
  signIn,
  fetchUserAttributes,
  fetchAuthSession,
  signOut,
  confirmSignIn,
} from 'aws-amplify/auth'

const MFA_STEPS = [
  'CONFIRM_SIGN_IN_WITH_SMS_CODE',
  'CONFIRM_SIGN_IN_WITH_TOTP_CODE',
  'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE',
] as const

export const isMfaStep = (step?: string) => {
  if (!step) return false

  return (
    MFA_STEPS.includes(step as (typeof MFA_STEPS)[number]) ||
    step.includes('MFA') ||
    step.includes('OTP') ||
    step.includes('CHALLENGE')
  )
}

/** Returned by loginAction when user must complete MFA on the login page */
export const LOGIN_MFA_REQUIRED = 'mfa_required' as const

const initialState: AuthState = {
  isLoggedIn: false,
  user: undefined,
  errorMsg: undefined,
  isLoggingOut: false,
  mfa: undefined,
  mfaAttempts: 0,
}

const loginSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginSuccess: (state, action: PayloadAction<MikaUser>) => {
      state.isLoggedIn = true
      state.user = action.payload
    },
    logoutUser: state => {
      state.user = undefined
      state.isLoggedIn = false
    },
    loginError: (state, action: PayloadAction<string>) => {
      state.errorMsg = action.payload
    },
    setLoggingOut: (state, action: PayloadAction<boolean>) => {
      state.isLoggingOut = action.payload
    },
    setMfaChallenge: (state, action: PayloadAction<MfaChallengeState>) => {
      state.mfa = action.payload
      state.mfaAttempts = 0
    },
    clearMfaChallenge: state => {
      state.mfa = undefined
      state.mfaAttempts = 0
    },
    incrementMfaAttempts: state => {
      state.mfaAttempts = (state.mfaAttempts || 0) + 1
    },
  },
})

const groupMap = {
  mikaAdmin: '',
  portfolioAdmin: 'custom:portfolioId',
  servicePortfolioAdmin: 'custom:servicePortfolioId',
  orgAdmin: 'custom:orgId',
  campaignAdmin: 'custom:campaignId',
}

export const loginAction =
  (
    username: string,
    password: string,
    confirmLogin?: boolean,
  ): ThunkAction<
    Promise<boolean | typeof LOGIN_MFA_REQUIRED>,
    AuthState,
    null,
    PayloadAction<MikaUser | string | MfaChallengeState | undefined>
  > =>
  async dispatch => {
    try {
      if (!confirmLogin) {
        dispatch(clearMfaChallenge())
      }

      const normalizedUsername = (username || '').toLowerCase()
      const signInStatus =
        !confirmLogin &&
        (await signIn({
          username: normalizedUsername,
          password,
          options: {
            authFlowType: 'CUSTOM_WITH_SRP',
          },
        }))
      if (
        !confirmLogin &&
        signInStatus?.nextStep?.signInStep ===
          'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
      ) {
        const user = {
          email: username,
          email_verified: '',
          first_login: true,
          roles: [],
          accessToken: '',
          userId: '',
          username,
        }

        dispatch(loginSuccess(user))
        datadogRum.setUser({
          id: user.userId,
          email: user.email,
          name: user.username,
        })
        return false
      }

      if (
        !confirmLogin &&
        signInStatus?.nextStep?.signInStep &&
        isMfaStep(signInStatus.nextStep.signInStep)
      ) {
        dispatch(
          setMfaChallenge({
            required: true,
            method: 'TOTP',
            destination: normalizedUsername,
          }),
        )
        dispatch(loginError(''))
        return LOGIN_MFA_REQUIRED
      }

      if (!confirmLogin && !signInStatus?.isSignedIn) {
        if (signInStatus?.nextStep?.signInStep === 'RESET_PASSWORD')
          throw new Error('PasswordResetRequiredException')
        if (signInStatus?.nextStep?.signInStep)
          throw new Error(signInStatus?.nextStep.signInStep)
      }

      if (signInStatus?.isSignedIn || confirmLogin) {
        // After first-login password change, give Amplify a moment to persist the new session
        // before we read it (avoids race where fetchAuthSession runs before cache is updated).
        if (confirmLogin) {
          await new Promise(resolve => setTimeout(resolve, 300))
        }
        const userAttributes = await fetchUserAttributes()
        // After first-login password change, get session (try forceRefresh only if needed).
        let authenticatedSession = await fetchAuthSession()
        if (
          confirmLogin &&
          !authenticatedSession?.tokens?.accessToken
        ) {
          authenticatedSession = await fetchAuthSession({
            forceRefresh: true,
          })
        }
        const accessJwt = authenticatedSession?.tokens?.accessToken?.toString()
        if (
          confirmLogin &&
          !accessJwt
        ) {
          throw new Error(
            'SessionNotReadyAfterPasswordChange',
          )
        }
        if (userAttributes) {
          const orgId: string =
            userAttributes['custom:orgId']?.toString() ||
            authenticatedSession?.tokens?.accessToken?.payload[
              'cognito:orgId'
            ]?.toString()

          const portfolioId: string =
            userAttributes['custom:portfolioId']?.toString()
          const servicePortfolioId: string =
            userAttributes['custom:servicePortfolioId']?.toString()

          const scope = userAttributes['custom:scope']

          const roles: string[] = authenticatedSession?.tokens?.accessToken
            ?.payload['cognito:groups'] as string[]

          const isGroupValid = roles?.every(
            role => groupMap[role] !== undefined,
          )

          if (!isGroupValid) throw new Error('UnknownGroupException')

          const isPayloadValid =
            roles.every(role => userAttributes[groupMap[role]]) ||
            roles.includes('mikaAdmin')

          if (!isPayloadValid)
            throw new Error('CustomPropertyNotMatchException')

          const user = {
            orgId: orgId,
            portfolioId: portfolioId,
            servicePortfolioId: servicePortfolioId,
            userId: userAttributes.sub,
            roles: roles,
            accessToken: accessJwt,
            tokenExpiration:
              authenticatedSession?.credentials.expiration.getTime(),
            email: userAttributes.email,
            email_verified: userAttributes.email_verified,
            name: userAttributes.name,
            username: userAttributes.name,
            scope,
          }

          dispatch(clearMfaChallenge())
          dispatch(loginSuccess(user))
          datadogRum.setUser({
            id: user.userId,
            email: user.email,
            name: user.name,
          })
          return true
        }
      }
      return false
    } catch (error: any) {
      let errorMessage: string
      // Amplify/Cognito set exception type on error.name; message is the description
      const exceptionType = error?.name ?? error?.message ?? ''
      console.log(error)

      switch (exceptionType) {
        case 'NotAuthorizedException':
        case 'UserNotFoundException':
          errorMessage = 'Login failed, check your e-mail or password.'
          if (error?.message?.includes('revoked'))
            errorMessage = 'Session expired, please login again.'
          break
        case 'PasswordResetRequiredException':
          window.location.href = `/password-reset/${username}`
          return
        case 'CustomPropertyNotMatchException':
          errorMessage = 'Custom properties are not matching with the group.'
          break
        case 'UnknownGroupException':
          errorMessage = 'You are not allowed to use the portal.'
          break
        case 'SessionNotReadyAfterPasswordChange':
          errorMessage =
            'Password was updated. Please sign in again with your new password.'
          break
        case '':
          errorMessage = ''
          break
        default:
          errorMessage = 'Could not authenticate, validate your informations.'
          if (process.env.NODE_ENV === 'development') {
            console.error('[login]', exceptionType, error?.message, error)
          }
      }

      dispatch(loginError(errorMessage))
      setTimeout(() => {
        dispatch(loginError(''))
      }, 3000)
      return false
    }
  }

export const confirmMfaAction =
  (
    code: string,
  ): ThunkAction<
    Promise<void>,
    AuthState,
    null,
    PayloadAction<MikaUser | string | MfaChallengeState | undefined>
  > =>
  async (dispatch, getState) => {
    try {
      await confirmSignIn({ challengeResponse: code })
      await dispatch(loginAction('', '', true))
    } catch (error: any) {
      const currentState = getState() as unknown as RootState
      const currentAttempts = currentState.login?.mfaAttempts || 0
      const newAttempts = currentAttempts + 1

      dispatch(incrementMfaAttempts())

      if (newAttempts >= 1) {
        dispatch(clearMfaChallenge())
        dispatch(
          loginError(
            'Maximum number of attempts exceeded. Please try logging in again.',
          ),
        )
        setTimeout(() => {
          dispatch(loginError(''))
        }, 3000)
        await signOut()
        window.location.href = '/'
        return
      }

      let errorMessage = 'Invalid verification code.'

      switch (error?.name) {
        case 'ExpiredCodeException':
          errorMessage = 'Verification code expired, request a new one.'
          break
        case 'CodeMismatchException':
          errorMessage = 'Verification code is incorrect.'
          break
        default:
          errorMessage = 'Could not confirm the verification code.'
      }

      dispatch(loginError(errorMessage))
      setTimeout(() => {
        dispatch(loginError(''))
      }, 3000)
    }
  }

export const resendMfaCodeAction =
  (
    username: string,
    password: string,
  ): ThunkAction<
    Promise<void>,
    AuthState,
    null,
    PayloadAction<MikaUser | string | MfaChallengeState | undefined>
  > =>
  async dispatch => {
    try {
      dispatch(loginError(''))
      const normalizedUsername = (username || '').toLowerCase()
      const signInStatus = await signIn({
        username: normalizedUsername,
        password,
        options: {
          authFlowType: 'CUSTOM_WITH_SRP',
        },
      })

      if (
        signInStatus?.nextStep?.signInStep &&
        isMfaStep(signInStatus.nextStep.signInStep)
      ) {
        dispatch(
          setMfaChallenge({
            required: true,
            method: 'TOTP',
            destination: normalizedUsername,
          }),
        )
        dispatch(loginError(''))
      } else if (signInStatus?.isSignedIn) {
        await dispatch(loginAction('', '', true))
      } else {
        throw new Error('Could not resend verification code.')
      }
    } catch (error: any) {
      let errorMessage = 'Could not resend verification code.'

      switch (error?.message) {
        case 'NotAuthorizedException':
        case 'UserNotFoundException':
          errorMessage = 'Invalid credentials. Please try again.'
          break
        default:
          errorMessage = 'Could not resend verification code.'
      }

      dispatch(loginError(errorMessage))
      setTimeout(() => {
        dispatch(loginError(''))
      }, 3000)
    }
  }

export const logout = () => async (dispatch: Dispatch) => {
  try {
    dispatch(setLoggingOut(true))
    await signOut()
    dispatch(logoutUser())
    datadogRum.setUser(undefined)
    window.location.href = '/'
    dispatch(setUserList([]))
    dispatch(setOrganizationList([]))
    dispatch(setSelectedOrganization(null))
  } catch (error) {
    console.error('Failed to logout: ', error)
  } finally {
    dispatch(setLoggingOut(false))
  }
}

export const {
  setLoggingOut,
  loginSuccess,
  logoutUser,
  loginError,
  setMfaChallenge,
  clearMfaChallenge,
  incrementMfaAttempts,
} = loginSlice.actions
export default loginSlice.reducer
